<template>
	<span class="field-label"
		>{{ leadingText }}<strong v-if="tagText" class="field-label__tag">{{ tagText }}</strong></span
	>
</template>

<script setup lang="ts">
	import { computed, watchEffect } from "vue";

	// Renders an input-field label with its trailing "(Required)" / "(Optional)"
	// tag in bold so it stands out (LSP-16777). It takes the WHOLE label string
	// and splits the tag off itself, so converting a field is just moving the
	// existing label value into the `#label` slot verbatim:
	//
	//   <v-text-field label="Name (Required)" />
	//   ->
	//   <v-text-field><template #label><FieldLabel label="Name (Required)" /></template></v-text-field>
	//
	// Labels with no tag (or a dynamic value that resolves to none) render plain,
	// so it is safe to wrap any label — including `:label="getRequiredLabel(f)"`.
	// The field name renders as-is, then a single normal space (not &nbsp;), then
	// the bold tag — so the normal "Name (Required)" case renders identically to
	// before and DOM text / selectors that match it still match. Whitespace
	// immediately around the tag is normalized to that single space.
	const props = defineProps<{
		label?: string | null;
	}>();

	// Matches a trailing "(Required)" or "(Optional)", with any leading whitespace,
	// at the very end of the label.
	const TAG_PATTERN = /\s*(\((?:Required|Optional)\))\s*$/;

	// String()-coerce before matching: props.label is typed `string | null`, but a caller building the
	// label dynamically from untrusted/malformed data (e.g. an LLM/MCP-driven form schema field) can
	// still hand this a number/object/array at runtime. `.match()` on a non-string throws and crashes
	// the whole render tree — coercing first degrades to visible text instead (break-it round 5).
	//
	// Coerced exactly ONCE here, then reused by every computed below (break-it round 6): a naive
	// String(props.label) call repeated in each computed would re-invoke a custom .toString() once
	// per call, and a stateful/impure .toString() could then hand `match`/`tagText` a different
	// string than `leadingText` sees, bolding a tag that doesn't match what's actually rendered as
	// the leading text. A single shared snapshot makes that impossible by construction.
	const labelText = computed(() => String(props.label ?? ""));
	const match = computed(() => labelText.value.match(TAG_PATTERN));
	const tagText = computed(() => match.value?.[1] ?? "");

	// LSP-19494: a customer could not tell what they had to fill in, because some screens marked
	// every field "(Required)"/"(Optional)" and others marked none. This component is the one place
	// every field label passes through, so it is where an untagged label gets caught — in dev only,
	// as a warning, so a missing tag is visible while building the form rather than after QA finds
	// it. Never throws and never changes what renders: an untagged label still renders plain.
	//
	// Silent under the test runner on purpose. Plenty of labels reach this component dynamically (a
	// wrapper forwarding `$attrs.label`, a form-builder field named by the customer, a test fixture),
	// so those can never be made to carry a static tag; warning on them would turn the shared "no
	// unexpected console.warn" assertion into 200+ unrelated failures and say nothing about the
	// product. The static-label invariant is enforced in CI by
	// apps/contact-admin/tests/unit/components/fieldLabelMarkerGuard.spec.ts instead.
	const shouldWarnAboutMissingTag = Boolean(import.meta.env?.DEV) && import.meta.env?.MODE !== "test" && !import.meta.env?.VITEST;
	if (shouldWarnAboutMissingTag) {
		watchEffect(() => {
			const label = labelText.value.trim();
			if (label && !match.value) {
				console.warn(
					`FieldLabel: "${label}" has no "(Required)" or "(Optional)" tag. Every input carries one so the customer can tell what they must fill in (LSP-19494).`
				);
			}
		});
	}
	const leadingText = computed(() => {
		const label = labelText.value;
		if (!match.value) return label;
		const head = label.slice(0, match.value.index).trimEnd();
		// Keep the original single space between the name and the (now bold) tag.
		return head ? `${head} ` : "";
	});
</script>

<style scoped>
	.field-label__tag {
		font-weight: 700;
	}
</style>
